import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSearch, FiPlusCircle, FiEdit2, FiTrash2, FiAward, FiExternalLink } from "react-icons/fi";
import scholarshipService from "../../services/scholarshipService";
import { useApp } from "../../context/AppContext";

const STATUS_FILTERS = ["All", "Applied", "Approved", "Disbursed", "Rejected"];
const STATUS_BADGE = {
    Applied: <span className="badge" style={{ background: "#e2e8f0", color: "#475569" }}>Applied</span>,
    Approved: <span className="badge" style={{ background: "#fef3c7", color: "#b45309" }}>Approved</span>,
    Disbursed: <span className="badge badge-green">Disbursed</span>,
    Rejected: <span className="badge" style={{ background: "#fee2e2", color: "#b91c1c" }}>Rejected</span>,
};

export default function ScholarshipList() {
    const navigate = useNavigate();
    const { academicYear } = useApp();

    const [scholarships, setScholarships] = useState([]);
    const [status, setStatus] = useState("All");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchScholarships = async (statusValue = status, searchValue = search, pageValue = page) => {
        setLoading(true);
        try {
            const params = { search: searchValue, page: pageValue, limit: 20 };
            if (academicYear?._id) params.academicYear = academicYear._id;
            if (statusValue !== "All") params.status = statusValue;
            const res = await scholarshipService.getAll(params);
            setScholarships(res.data.scholarships || []);
            setPages(res.data.pages || 1);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchScholarships(status, search, page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, page, academicYear?._id]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            fetchScholarships(status, search, 1);
        }, 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await scholarshipService.remove(deleteTarget._id);
            toast.success("Scholarship record deleted.");
            setDeleteTarget(null);
            fetchScholarships(status, search, page);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Scholarships</h1>
                    <p className="page-subtitle">
                        Track scholarship applications, sanctions, and disbursements
                        {academicYear?.label ? ` for ${academicYear.label}` : ""}.
                    </p>
                </div>
                <button className="btn btn-primary" onClick={() => navigate("/scholarships/add")}>
                    <FiPlusCircle size={14} style={{ marginRight: 6 }} />
                    Add Scholarship
                </button>
            </div>

            <div className="card">
                <div
                    className="card-header"
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem" }}
                >
                    <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                        {STATUS_FILTERS.map((f) => (
                            <button
                                key={f}
                                type="button"
                                className={`btn btn-sm ${status === f ? "btn-primary" : "btn-outline"}`}
                                onClick={() => {
                                    setStatus(f);
                                    setPage(1);
                                }}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                    <div className="search-bar" style={{ width: 260 }}>
                        <FiSearch size={14} color="var(--text-muted)" />
                        <input
                            placeholder="Search student, enrollment no, or scheme..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="card-body" style={{ padding: 0 }}>
                    {loading ? (
                        <div className="loading-center">
                            <div className="spinner" />
                            Loading scholarships...
                        </div>
                    ) : scholarships.length === 0 ? (
                        <div className="empty-state">
                            <FiAward />
                            <p>No scholarship records found.</p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Student</th>
                                        <th>Scheme</th>
                                        <th>Sanctioned</th>
                                        <th>Disbursed</th>
                                        <th>Status</th>
                                        <th>Proof</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scholarships.map((s) => (
                                        <tr key={s._id}>
                                            <td>
                                                <div style={{ fontWeight: 600 }}>{s.student?.fullName || "—"}</div>
                                                <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{s.student?.enrollmentNo}</div>
                                            </td>
                                            <td>{s.schemeType}</td>
                                            <td>₹{s.sanctionedAmount.toLocaleString("en-IN")}</td>
                                            <td>₹{s.disbursedAmount.toLocaleString("en-IN")}</td>
                                            <td>{STATUS_BADGE[s.status] || s.status}</td>
                                            <td>
                                                {s.sanctionLetterUrl ? (
                                                    <a href={s.sanctionLetterUrl} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">
                                                        <FiExternalLink size={12} />
                                                    </a>
                                                ) : (
                                                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>—</span>
                                                )}
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", gap: "0.4rem", justifyContent: "flex-end" }}>
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        title="Edit"
                                                        onClick={() => navigate(`/scholarships/${s._id}/edit`)}
                                                    >
                                                        <FiEdit2 size={13} />
                                                    </button>
                                                    <button className="btn btn-outline btn-sm" title="Delete" onClick={() => setDeleteTarget(s)}>
                                                        <FiTrash2 size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {pages > 1 && (
                    <div className="pagination">
                        {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                            <button key={p} className={p === page ? "active" : ""} onClick={() => setPage(p)}>
                                {p}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {deleteTarget && (
                <div className="modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Delete Scholarship Record</h3>
                        <p>
                            Delete the <strong>{deleteTarget.schemeType}</strong> record for{" "}
                            <strong>{deleteTarget.student?.fullName}</strong>? This will also recalculate their scholarship balance.
                        </p>
                        <div className="modal-actions">
                            <button className="btn btn-outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                                Cancel
                            </button>
                            <button className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
                                {deleting ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}